import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { LayerGroup, Polyline } from 'react-leaflet';

import turfCut from './turf-cut';
import * as TurfHelpers from '@turf/helpers';
import turfIntersect from '@turf/intersect';
import turfArea from '@turf/area';
import pointsWithinPolygon from '@turf/points-within-polygon';
import turfBuffer from '@turf/buffer';
import turfLength from '@turf/length';
import turfSimplify from '@turf/simplify';
import config from '~/config/base';

import $ from 'jquery';

const polylineStyle = {
  color: '#D14F4F',
  weight: 3,
  className: 'mapfeature--initial',
  clickable : false,
  pointerEvents: 'none'
};

const COLS = ['teal', 'orange', 'yellow', 'aqua', 'purple', 'fuchsia', 'maroon']
class MapDrawer extends PureComponent {
  state = {
    drawCoords: [],
    isDrawing: false
  }

  constructor(props, context) {
    super();

    this.berlinPoly = TurfHelpers.polygon([props.berlin.features[0].geometry.coordinates[0]]);
    this.westArea = turfArea(props.westGeojson);
    this.innerBorderLength = turfLength(props.innerBorder);

    this.bindEvents(props.map);
    $('.pen-icon').show();
  }

  bindEvents(map) {
    map.on('mousedown', this.onMouseDown);
    map.on('mousemove', this.onMouseMove);
    map.on('mouseup', this.onMouseUp);

    $('.map-comp-wrapper').on('mouseleave', this.onMouseUp);
  }

  onMouseDown = (evt) => {
    evt.originalEvent.preventDefault();

    $('.pen-icon').hide();

    this.setState({
      drawCoords: [],
      isDrawing: true
    });
  }

  onMouseMove = (evt) => {
    evt.originalEvent.preventDefault();

    if (this.state.isDrawing) {
      this.setState({
        drawCoords: this.state.drawCoords.concat(evt.latlng)
      });
    }
  }

  onMouseUp = (evt) => {
    evt.originalEvent.preventDefault();

    this.props.onLoad(true);
    this.setState({ isDrawing: false });
    this.onFinished(evt);
  }

  onFinished = (evt) => {
    if (this.state.drawCoords.length === 0 || !this.drawLine){
      return this.props.onLoad(false);
    }

    const lineString = TurfHelpers.lineString(this.drawLine.leafletElement.toGeoJSON().geometry.coordinates);
    let cuts = turfCut(this.berlinPoly, lineString);

    const startAndEndPoints = [0, this.state.drawCoords.length - 1].map(index => TurfHelpers.point([this.state.drawCoords[index].lng, this.state.drawCoords[index].lat]));
    const startAndEndOutside = pointsWithinPolygon(startAndEndPoints, this.berlinPoly);

    if (startAndEndOutside.length) {
      this.props.onDrawError('Bitte achten Sie darauf, dass der Start- und Endpunkt außerhalb der Grenze liegen.');
      return this.props.onLoad(false);
    }

    if (cuts.features.length === 1 && cuts.features[0].geometry.coordinates.length >= 2) {
      const cutFeats = cuts.features[0].geometry.coordinates.map((coords, i) => TurfHelpers.polygon([coords]));
      cuts = TurfHelpers.featureCollection(cutFeats);
    }

    if (cuts.features.length !== 2) {
      this.props.onDrawError('Deutschland muss in zwei Flächen eingeteilt werden. Zeichnen Sie über die Deutschland-Grenze hinaus.');
      console.log('ERROR: Deutschland muss in zwei Flächen eingeteilt werden.');
      return this.props.onLoad(false);
    }

    this.calculateScore(lineString, cuts);
  }

  calculateScore(border, cuts) {
    const cutsSorted = cuts.features
      .map(feat => {
        const intersection = turfIntersect(this.props.westGeojson, feat);
        const points = intersection ? turfArea(intersection) : 0;

        const eastIntersection = turfIntersect(this.props.eastGeojson, feat);
        const penaltyPoints = eastIntersection ? turfArea(eastIntersection) : 0;
        const score = ((points - penaltyPoints) / this.westArea) * 100;

        feat.properties.score = score;

        return feat;
      })
      .filter(feat => feat.properties.score)
      .sort((a, b) => {
        if (a.properties.score > b.properties.score) {
          return -1;
        } else if (a.properties.score < b.properties.score) {
          return 1;
        }

        return 0;
      });

    if (!cutsSorted.length) {
      console.log('ERROR: Punkte konnten nicht berechnet werden -> Keine Überschneidungen');
      return this.props.onLoad(false);
    }

    map.off('mousedown', this.onMouseDown);
    map.off('mousemove', this.onMouseMove);
    map.off('mouseup', this.onMouseUp);
    $('.map-comp-wrapper').off('mouseleave', this.onMouseUp);

    const borderCoords = border.geometry.coordinates.map(coord => [+coord[0].toFixed(6), +coord[1].toFixed(6)]);
    const points = TurfHelpers.points(borderCoords);
    const ptsWithin = pointsWithinPolygon(points, turfBuffer(this.berlinPoly, 10));
    const clippedBorder = TurfHelpers.lineString(ptsWithin.features.map(feat => feat.geometry.coordinates));
    const clippedBorderSimplified = turfSimplify(clippedBorder, { tolerance: 0.001, highQuality: false });
    const clippedBorderCoordsS = clippedBorderSimplified.geometry.coordinates.map(coord => [+coord[0].toFixed(6), +coord[1].toFixed(6)]);

    const userBorderLength = turfLength(clippedBorder);
    const userBorderDiff = Math.abs(userBorderLength - this.innerBorderLength);
    const userBorderScore = (1 - (userBorderDiff / userBorderLength)) * 100;

    // console.log('area points', cutsSorted[0].properties.score);
    // console.log('border points', userBorderScore);
    // console.log('user line length', userBorderLength);
    // console.log('border length', this.innerBorderLength);
    // console.log('border length diff', userBorderDiff);

    const score = userBorderDiff > 400 ? (cutsSorted[0].properties.score + userBorderScore) / 2 : cutsSorted[0].properties.score;

    if (config.isStatic) {
      $.getJSON('data/server-response.json')
        .done(res => {
          this.props.onDrawingFinished(res, score, this.state.drawCoords);
        })
        .catch(err => this.props.onLoad(false));
    } else {
      // if you want to use the server, use this:
      $.post('https://data.interaktiv.cloud.funkedigital.de/api/mapdraw/innerdeutsche-grenze', { coords: JSON.stringify(clippedBorderCoordsS), score })
        .done(res => {
          this.props.onDrawingFinished(res, score, this.state.drawCoords);
        })
        .catch(err => this.props.onLoad(false));
    }
  }

  convertKm(km) {
    return +((km / 1000000).toFixed(3));
  }

  render() {
    return (
      <LayerGroup>
        {this.state.drawCoords.length > 1 &&
          <Polyline
            ref={ref => {this.drawLine = ref;}}
            positions={this.state.drawCoords}
            color="#D14F4F"
          />
        }
      </LayerGroup>
    );
  }
}

MapDrawer.contextTypes = {
  map: PropTypes.any
}

export default MapDrawer;

